import { RemediationProfile } from 'src/app/models/remediation-profile/remediation-profile';
import { AudioProfileType } from 'src/app/models/remediation-profile/audio-profile';
import { Remediation } from 'src/app/models/remediation-profile/remediation';
import { StreamTier, TIER_CONFIGS } from 'src/app/models/stream-tier/stream-tier';
import { LoggingChannels, LoggingService } from '../logging/logging.service';

export class RemediationProfileGenerator {

  constructor(
    private logging: LoggingService,
  ) {}

  /**
   * This function generates the dynamic remediation profiles based on the current camera settings.
   *
   * The first step of the algorithm is to generate profiles up to lowest resolutions with fps of 60, 30 and 24.
   *
   * The second step is to lower fps to 18 and then lower the audio bitrate gradually.
   */
  generateProfiles(remediation: Remediation, sharing: boolean, userId: string, roomId: string): void {
    let width = remediation.max.width;
    let height = remediation.max.height;
    let framerate = remediation.max.framerate;
    remediation.profiles = [];
    const profiles = [];
    do {
      profiles.push(`${width}-${height}-${framerate}-${AudioProfileType.high}`);
      remediation.profiles.push(new RemediationProfile(width, height, framerate, AudioProfileType.high));
      if (framerate > 30) {
        framerate = 30;
      } else if (framerate > 24) {
        framerate = 24;
      } else {
        framerate = remediation.max.framerate;
        width = Math.round(width / (sharing ? 1.25 : 1.5));
        height = Math.round(height / (sharing ? 1.25 : 1.5));
      }
    } while (width >= (sharing ? 800 : 320) || height >= (sharing ? 600 : 240));
    do {
      profiles.push(`${width}-${height}-${framerate}-${AudioProfileType.high}`);
      remediation.profiles.push(new RemediationProfile(width, height, framerate, AudioProfileType.high));
      if (framerate > 30) {
        framerate = 30;
      } else if (framerate > 24) {
        framerate = 24;
      } else {
        framerate = 18;
        profiles.push(`${width}-${height}-${framerate}-${AudioProfileType.high}`);
        remediation.profiles.push(new RemediationProfile(width, height, framerate, AudioProfileType.high));
        profiles.push(`${width}-${height}-${framerate}-${AudioProfileType.good}`);
        remediation.profiles.push(new RemediationProfile(width, height, framerate, AudioProfileType.good));
        profiles.push(`${width}-${height}-${framerate}-${AudioProfileType.medium}`);
        remediation.profiles.push(new RemediationProfile(width, height, framerate, AudioProfileType.medium));
        profiles.push(`${width}-${height}-${framerate}-${AudioProfileType.low}`);
        remediation.profiles.push(new RemediationProfile(width, height, framerate, AudioProfileType.low));
      }
    } while (framerate >= 24);
    this.logging.log('IssueRemediationService: available profiles', LoggingChannels.Remediation, profiles, sharing);
    if (sharing) {
      this.logging.logCustomEvent('profiles', {
        userId,
        roomId,
        profiles,
        sharing: true,
      });
    } else {
      this.logging.logCustomEvent('profiles', {
        userId,
        roomId,
        profiles,
        sharing: false,
      });
    }
  }

  generateProfilesForTier(tier: StreamTier): RemediationProfile[] {
    const config = TIER_CONFIGS[tier];
    if (!config.sendVideo) {
      return [new RemediationProfile(0, 0, 0, AudioProfileType.high)];
    }
    const profiles: RemediationProfile[] = [];
    let width = config.maxWidth;
    let height = config.maxHeight;
    let framerate = config.maxFps;
    do {
      profiles.push(new RemediationProfile(width, height, framerate, AudioProfileType.high));
      if (framerate > 24) {
        framerate = 24;
      } else {
        width = Math.round(width / 1.5);
        height = Math.round(height / 1.5);
        framerate = config.maxFps;
      }
    } while (width >= 160 && height >= 120);
    return profiles;
  }

  checkLocalProfile(
    remediation: Remediation,
    sharing: boolean,
    setLocalProfile: (participant: string, profile: RemediationProfile, sharing: boolean) => void,
  ): void {
    Object.keys(remediation.local).forEach((participant) => {
      let found = false;
      for (const profile of remediation.profiles) {
        if (profile.id === remediation.local[participant].id) {
          found = true;
        }
      }
      if (!found) {
        remediation.last[participant] = Date.now();
        setLocalProfile(participant, remediation.profiles[0], sharing);
      }
    });
  }

  setMaxProfile(
    width: number,
    height: number,
    framerate: number,
    sharing: boolean,
    participantRemediations: Remediation,
    shareRemediations: Remediation,
    userId: string,
    roomId: string,
    setLocalProfile: (participant: string, profile: RemediationProfile, sharing: boolean) => void,
  ): RemediationProfile {
    framerate = Math.round(framerate);
    let screenShareProfile: RemediationProfile = null;
    if (sharing) {
      shareRemediations.max.width = width;
      shareRemediations.max.height = height;
      shareRemediations.max.framerate = framerate;
    } else {
      participantRemediations.max.width = width;
      participantRemediations.max.height = height;
      participantRemediations.max.framerate = framerate;
      screenShareProfile = new RemediationProfile(253, Math.floor(253 * height / width), framerate, AudioProfileType.high);
    }
    this.logging.log('IssueRemediationService: max profile', LoggingChannels.Remediation, width, height, framerate, sharing);

    const remediation = sharing ? shareRemediations : participantRemediations;
    this.generateProfiles(remediation, sharing, userId, roomId);
    this.checkLocalProfile(remediation, sharing, setLocalProfile);

    return screenShareProfile;
  }
}
